/**
 * App initialization loading screen.
 *
 * Renders a neutral branded loading screen with a task checklist during startup.
 */
import type { ReactElement } from 'react';

import Spinner from '@/components/Spinner/Spinner';

export type LoadingTaskStatus = 'pending' | 'loading' | 'complete' | 'error';

export interface LoadingTask {
  id: string;
  label: string;
  status: LoadingTaskStatus;
  error?: string;
}

export interface AppLoaderProps {
  tasks: LoadingTask[];
  error?: string | null;
  onRetry?: () => void;
}

function TaskIcon({ status }: { status: LoadingTaskStatus }): ReactElement {
  switch (status) {
    case 'loading':
      return <Spinner size="sm" />;
    case 'complete':
      return (
        <svg className="h-5 w-5 text-emerald-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'error':
      return (
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
            clipRule="evenodd"
          />
        </svg>
      );
    default:
      return <div className="h-5 w-5 rounded-full bg-slate-700/40" aria-hidden="true" />;
  }
}

function TaskItem({ task }: { task: LoadingTask }): ReactElement {
  const textColor =
    task.status === 'complete'
      ? 'text-slate-300'
      : task.status === 'error'
        ? 'text-red-400'
        : task.status === 'loading'
          ? 'text-slate-100'
          : 'text-slate-500';

  return (
    <li className="flex items-center gap-3">
      <div className="flex h-6 w-6 items-center justify-center">
        <TaskIcon status={task.status} />
      </div>
      <span className={`text-sm ${textColor}`}>{task.label}</span>
      {task.error && <span className="text-xs text-red-400/70">— {task.error}</span>}
    </li>
  );
}

export default function AppLoader({ tasks, error, onRetry }: AppLoaderProps): ReactElement {
  const hasError = !!error || tasks.some((t) => t.status === 'error');

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-950">
      <div className="relative z-10 flex flex-col items-center">
        <div className="mb-8">
          <h1 className="font-display text-4xl font-bold tracking-wider text-(--text-primary)">Jums Dashboard</h1>
        </div>

        <div className="mb-6 min-w-72 rounded-xl border border-white/5 bg-slate-900/50 p-6">
          <ul className="space-y-3" role="list" aria-label="Loading tasks">
            {tasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </ul>
        </div>

        {hasError ? (
          <>
            <div className="mb-6 max-w-md rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-center">
              <p className="text-sm font-medium text-red-400">Failed to initialize application</p>
              {error && <p className="mt-1 text-xs text-red-400/70">{error}</p>}
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="rounded-full bg-indigo-500 px-6 py-2 font-medium text-white transition-colors hover:bg-indigo-600"
              >
                Retry
              </button>
            )}
          </>
        ) : (
          <p className="mt-4 max-w-xs text-center text-xs text-slate-500">Loading. This may take a moment on first load.</p>
        )}
      </div>
    </div>
  );
}
